/**
 * Renders the agent's `show_table` output as a real table, so query results
 * reach the user as structured rows instead of markdown retyped by the model.
 *
 * The payload is the tool output (server-built from query results), so only a
 * completed, well-formed output renders; anything else shows a short error.
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import DataTable, { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow } from '@amzn/meridian/data-table';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { defaultFormatCell, formatPercent } from 'src/utils/dashboardUtils';
import { formatUtcDate } from 'src/utils/formatDateTime';
import { type ChatMessageProps } from './types';

type TableCellValue = string | number | null;

interface TableColumn { key: string; label: string; format: string; currency?: string }

interface TableOutput {
  title: string;
  caption?: string;
  columns: TableColumn[];
  rows: TableCellValue[][];
  truncated?: boolean;
}

const NUMERIC_FORMATS = new Set(['integer', 'decimal', 'percent', 'currency']);
const TABLE_FORMATS = new Set(['string', 'integer', 'decimal', 'percent', 'currency', 'date']);

const isTableColumn = (value: unknown): value is TableColumn => {
  const column = value as Partial<TableColumn> | null;
  if (typeof column?.key !== 'string' || column.key === '' || typeof column.label !== 'string') return false;
  if (typeof column.format !== 'string' || !TABLE_FORMATS.has(column.format)) return false;
  return column.format !== 'currency' || (typeof column.currency === 'string' && /^[A-Z]{3}$/.test(column.currency));
};

const isTableCell = (value: unknown): value is TableCellValue =>
  value === null || typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value));

const isTableOutput = (value: unknown): value is TableOutput => {
  const table = value as Partial<TableOutput> | null;
  if (typeof table?.title !== 'string' || table.title.trim() === '') return false;
  if (!Array.isArray(table.columns) || !Array.isArray(table.rows)) return false;
  const columns: unknown[] = table.columns;
  if (columns.length === 0 || !columns.every(isTableColumn)) return false;
  if (new Set(columns.map(({ key }) => key)).size !== columns.length) return false;
  return table.rows.every((row: unknown) =>
    Array.isArray(row) && row.length === columns.length && row.every(isTableCell));
};

const formatCurrency = (value: TableCellValue, currency = ''): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value));

const formatTableCell = (value: TableCellValue, column: TableColumn): string => {
  if (value == null) return '--';
  if (NUMERIC_FORMATS.has(column.format) && typeof value !== 'number') return '--';
  switch (column.format) {
    case 'integer':
    case 'decimal':
      return defaultFormatCell(value, column.format);
    case 'percent':
      return formatPercent(value);
    case 'currency':
      return formatCurrency(value, column.currency);
    case 'date':
      return formatUtcDate(String(value), String(value));
    default:
      return String(value);
  }
};

export const TableMessage: React.FC<ChatMessageProps> = ({ part, index }) => {
  const { t } = useTranslation();
  const titleId = React.useId();
  if (part.state !== 'output-available' && part.state !== 'output-error') return null;
  const table = part.state === 'output-available' && isTableOutput(part.output) ? part.output : null;
  if (!table) return <Text color="error" data-testid="show-table-error" key={index} type="b100">{t('chat_table_error')}</Text>;
  return (
    <Column key={index} spacing="200" width="100%">
      <Text id={titleId} type="h100">{table.title}</Text>
      <div data-testid="chat-table" style={{ maxHeight: 400, overflowX: 'auto', overflowY: 'auto', width: '100%' }}>
        <DataTable aria-labelledby={titleId} showRowDividers={true} size="small" stickyHeaderRows={true} width="100%">
          <DataTableHead backgroundColor="secondary">
            <DataTableRow>
              {table.columns.map((column) => (
                <DataTableHeader key={column.key} textAlign={NUMERIC_FORMATS.has(column.format) ? 'end' : 'start'}>{column.label}</DataTableHeader>
              ))}
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {table.rows.length === 0 && (
              <DataTableRow>
                <DataTableCell colSpan={table.columns.length}>{t('table_no_results')}</DataTableCell>
              </DataTableRow>
            )}
            {table.rows.map((row, rowIndex) => (
              <DataTableRow key={rowIndex}>
                {table.columns.map((column, columnIndex) => (
                  <DataTableCell key={column.key} textAlign={NUMERIC_FORMATS.has(column.format) ? 'end' : 'start'}>
                    {formatTableCell(row[columnIndex], column)}
                  </DataTableCell>
                ))}
              </DataTableRow>
            ))}
          </DataTableBody>
        </DataTable>
      </div>
      {table.caption && <Text color="secondary" type="b200">{table.caption}</Text>}
      {table.truncated && <Text color="secondary" type="b200">{t('chat_table_truncated', { count: table.rows.length })}</Text>}
    </Column>
  );
};
